import {
  defaultLayoutBinds,
  sanitizeLayoutFieldBinds,
  type LayoutFieldBinds,
} from '@/lib/layouts/layoutFieldBinds';
import { applyBrandKit } from '@/lib/layouts/applyBrandKit';
import { buildDefaults } from '@/lib/layouts/buildDefaults';
import { stripDeferredRandomFillers } from '@/lib/layouts/fillerPlaylist';
import { parsePersistedPostLayout } from '@/lib/layouts/persistPostLayout';
import type { PostLayoutConfig, PostLayoutKind } from '@/lib/layouts/types';
import {
  harvestBrandKitLayoutSource,
  type HarvestableKitData,
} from './harvestBrandKitLayoutSource';
import { ensureRequiredTemplateMedia } from './ensureRequiredTemplateMedia';

export interface AppliedTemplateLayout {
  postLayout: PostLayoutConfig | null;
  fieldBinds: LayoutFieldBinds;
}

const SINGLE_LAYOUT: AppliedTemplateLayout = {
  postLayout: null,
  fieldBinds: {},
};

/**
 * Selecting a template adopts the kit's saved layout (kind + options + binds).
 * Kits without a parseable post_layout — the old seeded subtitle packs — apply
 * as Single (no post frame). Layout-picker identity overlay is a separate path.
 */
export function applyTemplateToEditor(
  _current: PostLayoutConfig | null,
  kitData: HarvestableKitData | null | undefined,
): AppliedTemplateLayout {
  const parsed = parsePersistedPostLayout(kitData?.post_layout);
  if (!parsed) return SINGLE_LAYOUT;

  let defaults: PostLayoutConfig;
  try {
    defaults = buildDefaults(parsed.postLayout.kind as PostLayoutKind, {});
  } catch {
    return SINGLE_LAYOUT;
  }

  const stripped = parsed.postLayout.kind === 'satisfatorio'
    || parsed.postLayout.kind === 'fofoca'
    || parsed.postLayout.kind === 'triple_split'
    || parsed.postLayout.kind === 'circulo'
    ? stripDeferredRandomFillers(parsed.postLayout)
    : parsed.postLayout;
  const postLayout = ensureRequiredTemplateMedia(applyBrandKit(
    {
      ...defaults,
      ...Object.fromEntries(
        Object.entries(stripped).filter(([, value]) => value !== undefined),
      ),
    } as PostLayoutConfig,
    harvestBrandKitLayoutSource(kitData),
  ));

  const fieldBinds = sanitizeLayoutFieldBinds(postLayout.kind, {
    ...defaultLayoutBinds(postLayout.kind),
    ...parsed.fieldBinds,
  });
  if (postLayout.kind === 'fofoca') {
    delete fieldBinds.videoTitle;
    delete fieldBinds.handle;
  }

  return { postLayout, fieldBinds };
}
